import { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { MdAdd, MdEdit } from 'react-icons/md'
import { useToast } from '../../../Hooks/ToasContext'
import { HCPFlowEnum, IHCPFlowStatus } from '../../../Services/HCP/flows/hcp-flow.interface'
import { ManageHCPFlowStatusUseCase } from '../../../Usecase/HCP/Flows/manage-hcp-flow-status-usecase'

interface IFlowManagementStatusDialogProps {
  open: boolean
  flow: HCPFlowEnum
  manageHCPFlowStatusUseCase: ManageHCPFlowStatusUseCase
  onClose: () => void
  onSaved: () => void
}

type DialogMode = 'list' | 'create' | 'edit'

const FLOW_LABELS: Partial<Record<HCPFlowEnum, string>> = {
  [HCPFlowEnum.PRODUCTION]: 'Produção',
  [HCPFlowEnum.MACHINE_PRODUCTION]: 'Produção de Máquinas',
  [HCPFlowEnum.LOGISTICS]: 'Logística',
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Erro ao salvar coluna.'
  const record = error as Record<string, unknown>
  return String(record.error ?? record.message ?? 'Erro ao salvar coluna.')
}

const FlowManagementStatusDialog: FC<IFlowManagementStatusDialogProps> = ({
  open,
  flow,
  manageHCPFlowStatusUseCase,
  onClose,
  onSaved,
}) => {
  const { showToast } = useToast()
  const [mode, setMode] = useState<DialogMode>('list')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [statuses, setStatuses] = useState<IHCPFlowStatus[]>([])
  const [editing, setEditing] = useState<IHCPFlowStatus | null>(null)
  const [name, setName] = useState('')
  const [sequence, setSequence] = useState('')
  const [isActive, setIsActive] = useState(true)

  const resetForm = () => {
    setName('')
    setSequence('')
    setIsActive(true)
    setEditing(null)
  }

  const loadStatuses = useCallback(async () => {
    setLoading(true)
    try {
      const result = await manageHCPFlowStatusUseCase.listStatuses(flow)
      if (result.isFailure) {
        showToast(getErrorMessage(result.error), 'error')
        return
      }
      const value = result.getValue() ?? []
      const items = [...value].sort((a, b) => a.sequence - b.sequence)
      setStatuses(items)
    } finally {
      setLoading(false)
    }
  }, [flow, manageHCPFlowStatusUseCase, showToast])

  useEffect(() => {
    if (!open) return
    setMode('list')
    resetForm()
    loadStatuses()
  }, [open, flow, loadStatuses])

  const openCreate = () => {
    resetForm()
    setMode('create')
  }

  const openEdit = (status: IHCPFlowStatus) => {
    setEditing(status)
    setName(status.name)
    setSequence(String(status.sequence))
    setIsActive(status.isActive)
    setMode('edit')
  }

  const handleSave = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      showToast('Informe o nome da coluna.', 'warning')
      return
    }

    const parsedSequence = sequence.trim() ? Number(sequence) : undefined
    if (parsedSequence !== undefined && (!Number.isFinite(parsedSequence) || parsedSequence <= 0)) {
      showToast('Sequência inválida.', 'warning')
      return
    }

    setSaving(true)
    try {
      const payload = {
        name: trimmedName,
        ...(parsedSequence !== undefined ? { sequence: parsedSequence } : {}),
        ...(mode === 'edit' ? { isActive } : {}),
      }

      const result = mode === 'edit' && editing
        ? await manageHCPFlowStatusUseCase.update(flow, editing.id, payload)
        : await manageHCPFlowStatusUseCase.create(flow, payload)

      if (result.isFailure) {
        showToast(getErrorMessage(result.error), 'error')
        return
      }

      showToast(mode === 'edit' ? 'Coluna atualizada.' : 'Coluna criada.', 'success')
      setMode('list')
      resetForm()
      await loadStatuses()
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const flowLabel = FLOW_LABELS[flow] ?? flow

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Colunas do fluxo — {flowLabel}
      </DialogTitle>

      <DialogContent dividers>
        {mode === 'list' && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Cadastre as etapas exibidas como colunas no Kanban.
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<MdAdd />}
                onClick={openCreate}
              >
                Nova coluna
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Seq.</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Ativa</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nenhuma coluna cadastrada. Crie a primeira coluna do fluxo.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {statuses.map((status) => (
                    <TableRow key={status.id} hover>
                      <TableCell>{status.sequence}</TableCell>
                      <TableCell>
                        <Chip label={status.displayCode} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{status.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={status.isActive ? 'Sim' : 'Não'}
                          size="small"
                          color={status.isActive ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar coluna">
                          <IconButton size="small" onClick={() => openEdit(status)}>
                            <MdEdit size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {(mode === 'create' || mode === 'edit') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {mode === 'create' ? 'Nova coluna' : `Editar — ${editing?.displayCode ?? ''}`}
            </Typography>

            <TextField
              label="Nome da coluna"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              placeholder="Ex.: Em produção"
            />

            <TextField
              label="Sequência (opcional)"
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              fullWidth
              size="small"
              type="number"
              helperText="Se vazio, o backend usa a próxima sequência disponível."
            />

            {mode === 'edit' && (
              <FormControlLabel
                control={(
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                )}
                label="Coluna ativa"
              />
            )}

            <Divider />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                onClick={() => {
                  setMode('list')
                  resetForm()
                }}
              >
                Voltar
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Fechar
        </Button>
        {(mode === 'create' || mode === 'edit') && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default FlowManagementStatusDialog
